import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import DateFormatter from 'components/DateFormatter';
import './mobile.css';

const DEFAULT_TABS = ['Overview', 'Audit'];

const getStatusClass = (value) => {
  const normalizedValue = `${value || ''}`.toLowerCase();

  if (
    normalizedValue.includes('active') &&
    !normalizedValue.includes('inactive')
  ) {
    return 'success';
  }

  if (normalizedValue.includes('inactive')) {
    return 'danger';
  }

  if (normalizedValue.includes('pending')) {
    return 'warning';
  }

  return 'muted';
};

const ProductMobileList = ({
  items,
  totalPages,
  currentPageNo,
  pageSize,
  handlePageChange,
  getTitle,
  getMeta,
  getSections,
  onTitleClick,
  renderActions,
  handleUserId,
}) => {
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeMobileTabs, setActiveMobileTabs] = useState({});

  const renderDetailValue = (detail) => {
    if (detail.control) {
      return (
        <div className="product-mobile-card__control-wrap">{detail.value}</div>
      );
    }

    if (detail.tone === 'status') {
      return (
        <span
          className={`product-mobile-card__pill ${getStatusClass(detail.value)}`}
        >
          {detail.value}
        </span>
      );
    }

    return detail.value;
  };

  const toggleMobileCard = (itemId) => {
    setExpandedCardId((prev) => (prev === itemId ? null : itemId));
  };

  const handleMobileTabChange = (itemId, tab) => {
    setActiveMobileTabs((prev) => ({
      ...prev,
      [itemId]: tab,
    }));
  };

  return (
    <>
      <div className="product-mobile-list">
        {(items || []).map((item, index) => {
          const sections = getSections(item, index);
          const tabs = Object.keys(sections).length
            ? Object.keys(sections)
            : DEFAULT_TABS;
          const activeTab = activeMobileTabs[item.id] || tabs[0];
          const activeSection = sections[activeTab] || [];
          const isExpanded = expandedCardId === item.id;
          const status = item?.status || 'N/A';
          const hasTitleClick = Boolean(onTitleClick);

          return (
            <div className="product-mobile-card" key={item.id}>
              <div className="product-mobile-card__summary">
                <button
                  type="button"
                  className={`product-mobile-card__identity ${hasTitleClick ? 'clickable' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (onTitleClick) {
                      onTitleClick(item);
                    }
                  }}
                >
                  <div className="product-mobile-card__name">
                    {getTitle(item)}
                  </div>
                  <div className="product-mobile-card__meta">
                    {getMeta(item)}
                  </div>
                </button>
                <div className="product-mobile-card__aside">
                  <span
                    className={`product-mobile-card__pill ${getStatusClass(status)}`}
                  >
                    {status}
                  </span>
                  <div className="product-mobile-card__tools">
                    <button
                      type="button"
                      className={`product-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleMobileCard(item.id);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? 'Collapse product details'
                          : 'Expand product details'
                      }
                    >
                      <FaChevronDown />
                    </button>
                    {renderActions ? renderActions(item, index) : null}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="product-mobile-card__body">
                  <div className="product-mobile-card__tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`product-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => handleMobileTabChange(item.id, tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="product-mobile-card__detail-list">
                    {activeSection.map((detail) => {
                      const isUserLink =
                        detail.type === 'createdUser' ||
                        detail.type === 'updatedUser';
                      const userId =
                        detail.type === 'createdUser'
                          ? item?.createdByUserId
                          : item?.updatedByUserId;

                      return (
                        <div
                          className="product-mobile-card__detail-row"
                          key={detail.label}
                        >
                          <div className="product-mobile-card__detail-label">
                            {detail.label}
                          </div>
                          <div
                            className={`product-mobile-card__detail-value ${
                              isUserLink && userId ? 'link' : ''
                            }`}
                            onClick={() => {
                              if (isUserLink && userId) {
                                handleUserId(item, userId);
                              }
                            }}
                            aria-hidden
                          >
                            {renderDetailValue(detail)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages ? (
        <div className="product-mobile-pagination">
          <span className="product-mobile-pagination__count">
            {currentPageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal product-mobile-pagination__select"
            value={pageSize}
            onChange={(e) => {
              const nextPageSize = Number(e.target.value);
              handlePageChange({
                pageNo: totalPages === currentPageNo ? 1 : currentPageNo,
                pageSize: nextPageSize,
              });
            }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="pagination product-mobile-pagination__actions">
            <button
              type="button"
              onClick={() => handlePageChange({ pageNo: 1, pageSize })}
              disabled={currentPageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: currentPageNo - 1,
                  pageSize,
                })
              }
              disabled={currentPageNo === 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: currentPageNo + 1,
                  pageSize,
                })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: totalPages,
                  pageSize,
                })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export const buildProductAuditSection = (item) => [
  {
    label: 'Created ID',
    value: item?.createdByUserId || 'N/A',
    type: 'createdUser',
  },
  {
    label: 'Updated ID',
    value: item?.updatedByUserId || 'N/A',
    type: 'updatedUser',
  },
];

export const createdAtDetail = (item) => ({
  label: 'Created On',
  value: <DateFormatter dateTime={item?.createdAt} />,
});

export default ProductMobileList;
